import React from "react";
import { Flash } from "~/types/flash";
import {
  WindowWithENV,
  WindowWithFrontChat,
  WindowWithGTM,
  WindowWithLibraries,
} from "~/types/global";
import { GA4LoginEvent } from "~/types/gtm";
import { User } from "~/types/user";
import { useHydrated } from "./useHydrated";

function recurseInit(
  callback: () => boolean,
  name: string,
  retries: number = 0,
) {
  const isSuccess = callback();

  if (isSuccess) return;

  if (retries > 4) {
    return;
  }

  setTimeout(() => recurseInit(callback, name, retries + 1), 500);
}

type Params = {
  cookieFlash: Flash | null;
  user: User | null;
  head: React.RefObject<HTMLHeadElement> | undefined;
  nonce: string;
};

const useStoreHydration = ({ cookieFlash, user, head, nonce }: Params) => {
  const isHydrated = useHydrated();
  const [flash, setFlash] = React.useState<Flash | null>(null);
  const [gtmID, setGTMID] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (!cookieFlash) setFlash(null);

    setFlash(cookieFlash);

    if (user && !user.doNotSellMyInformation) {
      const dataLayer = (window as WindowWithGTM)?.dataLayer;
      const event: GA4LoginEvent = {
        event: "ga4_login",
        ecommerce: {
          method: "Manual",
          customer_type: user.customerType,
        },
      };

      dataLayer?.push(event);
    }

    setTimeout(() => {
      setFlash(null);
    }, 3000);
  }, [cookieFlash, user]);

  React.useEffect(
    function initializeFrontChatEffect() {
      if (!isHydrated || user?.doNotSellMyInformation) return;

      function initFrontChat() {
        const FrontChat = (window as WindowWithFrontChat).FrontChat;
        const ENV = (window as WindowWithENV).ENV;

        if (!FrontChat || !ENV?.FRONTCHAT_ID) {
          return false;
        }

        FrontChat("init", { chatId: ENV.FRONTCHAT_ID });
        return true;
      }

      recurseInit(initFrontChat, "FrontChat");
    },
    [isHydrated],
  );

  React.useEffect(
    function initializeMerchantWidget() {
      if (!isHydrated || user?.doNotSellMyInformation) return;

      function initMerchantWidget() {
        const merchantwidget = (window as WindowWithLibraries).merchantwidget;

        if (!merchantwidget) return false;

        merchantwidget.start({ position: "LEFT_BOTTOM" });

        return true;
      }

      recurseInit(initMerchantWidget, "MerchantWidget");
    },
    [isHydrated],
  );

  React.useEffect(
    function initializeGTM() {
      const w = window as WindowWithENV;

      if (
        !isHydrated ||
        user?.doNotSellMyInformation ||
        !w.ENV?.GOOGLE_TAG_MANAGER_ID ||
        !head?.current ||
        !nonce ||
        gtmID
      )
        return;

      const gtmScript = document.createElement("script");
      gtmScript.setAttribute("nonce", nonce);
      gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-${w.ENV.GOOGLE_TAG_MANAGER_ID}');`;
      setGTMID(w.ENV.GOOGLE_TAG_MANAGER_ID);
      head.current.appendChild(gtmScript);
    },
    [isHydrated, head?.current, gtmID, nonce],
  );

  React.useEffect(() => {
    if (!user) return;
    sessionStorage.setItem("user", JSON.stringify(user));
  }, [user]);
  return {
    gtmID,
    flash,
  };
};

export default useStoreHydration;
